/**
 * Tenant Edit Page
 *
 * Page for editing an existing tenant.
 *
 * @fileoverview Admin tenant edit page
 */

import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  VStack,
  Skeleton,
  Alert,
  AlertIcon,
  AlertDescription,
  Button,
  useToast,
} from '@chakra-ui/react';
import { TenantForm } from '../../../components/admin';
import { useAdminTenant } from '../../../hooks/useAdminTenants';
import type { UpdateTenantInput } from '../../../types/admin';

/**
 * Tenant Edit Page Component
 *
 * Form page for editing an existing tenant.
 */
export const TenantEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const {
    tenant,
    loading: fetchLoading,
    error: fetchError,
    updateTenant,
  } = useAdminTenant(id!);

  const handleSubmit = async (data: UpdateTenantInput) => {
    try {
      const updated = await updateTenant(data);
      if (updated) {
        toast({
          title: 'Tenant updated',
          description: `${updated.name} has been updated successfully.`,
          status: 'success',
          duration: 3000,
        });
        navigate(`/admin/tenants/${id}`);
      }
    } catch (err) {
      // Error is already set in the hook
    }
  };

  const handleCancel = () => {
    navigate(`/admin/tenants/${id}`);
  };

  // Loading state
  if (fetchLoading) {
    return (
      <VStack spacing={6} align="stretch">
        <Box>
          <Skeleton h="32px" w="200px" mb={2} />
          <Skeleton h="20px" w="300px" />
        </Box>
        <Skeleton h="600px" borderRadius="lg" />
      </VStack>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          <AlertDescription>{fetchError}</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/admin/tenants')}>
          Back to Tenants
        </Button>
      </VStack>
    );
  }

  // Not found
  if (!tenant) {
    return (
      <VStack spacing={6} align="stretch">
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <AlertDescription>Tenant not found</AlertDescription>
        </Alert>
        <Button onClick={() => navigate('/admin/tenants')}>
          Back to Tenants
        </Button>
      </VStack>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header */}
      <Box>
        <Heading size="lg">Edit Tenant</Heading>
        <Text color="gray.500" mt={1}>
          Update {tenant.name}
        </Text>
      </Box>

      <TenantForm
        tenant={tenant}
        loading={fetchLoading}
        error={fetchError}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />
    </VStack>
  );
};

export default TenantEdit;
